import { WorkspaceScoped } from './workspace'
import { CursorPageQuery } from 'types/definitions'
import { PermissionSet } from 'iam/dtos/permissions'
import { NotificationPreferencesDTO } from './notification-preferences'
import { RoleDTO } from './role'
import { SkillDTO } from './skill'
import { UserDataType } from 'iam/models/UserDataType'

// Query for looking up workspace members.
export interface FindMembersQuery extends WorkspaceScoped, CursorPageQuery {
  search_text?: string

  /**
   * If specified, only returns members that contain any of the specified user types.
   */
  user_types?: UserDataType[]

  // requires a user to have a certain permission. This will not include workspace owners!
  // has_permission?: Permission;
}

export interface GetMemberPayload {
  workspace_id: string
  user_id: string
  user_public_id?: string
}

export interface GetMemberByPublicIdPayload {
  workspace_id: string
  user_public_id: string
}

export type GetManyMembersPayload = readonly GetMemberPayload[]

export type GetManyMembersByPublicIdPayload =
  readonly GetMemberByPublicIdPayload[]

export interface AddManySkillsToMemberPayload {
  workspace_id: string
  user_public_id: string
  skill_ids: string[]
}

export interface RemoveManySkillsToMemberPayload {
  workspace_id: string
  user_public_id: string
  skill_ids: string[]
}

export interface MemberDTO {
  /**
   * Member ID.
   */
  id: string
  /**
   * ID of the workspace this member belongs to.
   */
  workspace_id: string
  /**
   * The actual user ID.
   */
  user_id: string
  /**
   * The public user ID
   */
  user_public_id: string
  /**
   * Given name.
   */
  given_name: string
  /**
   * Family name.
   */
  family_name: string
  /**
   * Computed display name.
   */
  display_name: string
  /**
   * E-mail address.
   */
  email: string
  /**
   * Picture.
   */
  picture: string
  /**
   * Phone number.
   */
  phone: string
  /**
   * Max amount of jobs the member can pick up.
   */
  max_concurrent_provider_jobs: number
  /**
   * If `true`, the user cannot use the platform.
   */
  inactive: boolean
  /**
   * Permissions for the member.
   */
  member_permissions: PermissionSet
  /**
   * Rolled up permissions, takes roles and explicit member
   * permissions into account.
   */
  computed_permissions: PermissionSet
  /**
   * Workspace-specific notification preferences.
   */
  notification_preferences?: NotificationPreferencesDTO
  /**
   * The version of the terms that were agreed to.
   */
  approved_terms_version?: string
  /**
   * If the member signed up using a referral link,
   * the referrer is stored on the member.
   */
  referrer?: string
  /**
   * Additional meta to go with the referrer.
   */
  referrer_metadata?: any
  /**
   * Additional attributes defined by the workspace that the user
   * can edit.
   */
  member_metadata: MemberMetadataDTO
  /**
   * Additional attributes defined by the workspace that workspace admins
   * can edit.
   */
  workspace_metadata: any
  /**
   * Roles assigned to this member.
   */
  roles: Array<RoleMemberDTO>
  /**
   * Skills assigned to this member.
   */
  skills: Array<SkillMemberDTO>
  /**
   * Last time user logged into Workspace
   */
  last_login: string | null
  /**
   * Track user activity within a Workspace
   */
  last_seen: string | null
  /**
   * Date created.
   */
  date_created: string
  /**
   * Date modified.
   */
  date_modified: string
}

/**
 * Workspace Role Member, an assignment of a role to a member.
 */
export interface RoleMemberDTO {
  /**
   * ID.
   */
  id: string
  /**
   * Date created.
   */
  date_created: string
  /**
   * Date modified.
   */
  date_modified: string
  /**
   * The ID of the member.
   */
  member_id: string
  /**
   * The ID of the role.
   */
  role_id: string
  /**
   * Associated role.
   */
  readonly role: RoleDTO
}

/**
 * Assignment of a skill to a member.
 */
export interface SkillMemberDTO {
  /**
   * ID.
   */
  id: string
  /**
   * Date created.
   */
  date_created: string
  /**
   * Date modified.
   */
  date_modified: string
  /**
   * The member that the skill is assigned to.
   */
  member_id: string
  /**
   * Skill ID.
   */
  skill_id: string
  /**
   * The skill itself.
   */
  readonly skill: SkillDTO
}

export interface MemberMetadataDTO {
  [id: string]: unknown
}

/**
 * Payload for creating a member.
 */
export interface CreateMemberPayload {
  /**
   * The workspace ID.
   */
  workspace_id: string
  /**
   * The user ID.
   */
  user_id?: string
  /**
   * The given name.
   */
  given_name?: string
  /**
   * The family name.
   */
  family_name?: string
  /**
   * The email.
   */
  email?: string
  /**
   * The picture.
   */
  picture?: string
  /**
   * The phone number.
   */
  phone?: string
  /**
   * The max amount of jobs the member can pick up.
   */
  max_concurrent_provider_jobs?: number
  /**
   * If `true`, the user cannot use the platform.
   */
  inactive?: boolean
  /**
   * The permissions for the member.
   */
  member_permissions?: PermissionSet
  /**
   * If the member signed up using a referral link,
   * the referrer is stored on the member.
   */
  referrer?: string
  /**
   * Additional meta to go with the referrer.
   */
  referrer_metadata?: any
  /**
   * Additional attributes defined by the workspace that the user
   * can edit.
   */
  member_metadata?: MemberMetadata
  /**
   * Additional attributes defined by the workspace that workspace admins
   * can edit.
   */
  workspace_metadata?: any
  /**
   * User source.
   */
  source?: UserSource
  /**
   * Flag for whether a user needs to be prompted to complete registration
   */
  complete_registration_email?: boolean
  /**
   * The role ID.
   */
  role_id?: string
  /**
   * The list of skill IDs.
   */
  skill_ids?: string[]
  /**
   * The team ID.
   */
  team_id?: string
  /**
   * The team role ID.
   */
  team_role_id?: string
  /**
   * The portal to send the user to in the email.
   */
  portal?: Portal
}

export type CreateMemberManuallyPayload = CreateMemberPayload & {
  email: string
}

export type UpdateMemberPayload = Partial<MemberDTO>

export enum UserSource {
  Auth0 = 'AUTH0',
  Local = 'LOCAL',
  External = 'EXTERNAL',
}

export interface MemberMetadata {
  [id: string]: unknown
}

export enum Portal {
  CustomerPortal = 'CustomerPortal',
  WorkPortal = 'Portal',
}
